import React from "react";
import { Button, Form } from "components/ui/BootstrapCompat";
import {
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiX,
} from "react-icons/fi";

type WalletFilterBarProps = {
  searchTerm: string;
  filterType: string;
  filterStatus: string;
  dateFrom: string;
  dateTo: string;
  onSearchChange: (value: string) => void;
  onFilterTypeChange: (value: string) => void;
  onFilterStatusChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onRefetch: () => void;
  onExport: () => void;
  disableExport: boolean;
};

const typeOptions = [
  { value: "all", label: "All types" },
  { value: "DONATION", label: "Donation" },
  { value: "WITHDRAWAL", label: "Withdrawal" },
  { value: "ADJUSTMENT", label: "Adjustment" },
  { value: "TERMGAME_INCOME", label: "Termgame" },
];

const statusOptions = [
  { value: "all", label: "All status" },
  { value: "PENDING", label: "Pending" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELED", label: "Canceled" },
];

export default function WalletFilterBar({
  searchTerm,
  filterType,
  filterStatus,
  dateFrom,
  dateTo,
  onSearchChange,
  onFilterTypeChange,
  onFilterStatusChange,
  onDateFromChange,
  onDateToChange,
  onRefetch,
  onExport,
  disableExport,
}: WalletFilterBarProps) {
  const [showMobileFilters, setShowMobileFilters] = React.useState(false);
  const activeFilterCount = [
    filterType !== "all",
    filterStatus !== "all",
    Boolean(dateFrom),
    Boolean(dateTo),
  ].filter(Boolean).length;
  const hasAppliedValues = Boolean(searchTerm) || activeFilterCount > 0;

  const handleClearFilters = () => {
    onSearchChange("");
    onFilterTypeChange("all");
    onFilterStatusChange("all");
    onDateFromChange("");
    onDateToChange("");
    setShowMobileFilters(false);
  };

  return (
    <div className="mb-3 rounded-lg border border-[#dbe4ef] bg-white p-2 shadow-[0_8px_24px_rgba(16,24,40,0.05)] dark:border-slate-700 dark:bg-slate-900">
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
        <label className="relative block min-w-0 flex-1 md:max-w-[240px]">
          <span className="sr-only">Search transactions</span>
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Form.Control
            type="text"
            placeholder="ຄົ້ນຫາ..."
            className="h-11 min-w-0 rounded-lg border-[#d9e3ef] pl-10 text-[13px] font-semibold shadow-none dark:border-slate-700"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </label>

        <div className="grid grid-cols-[minmax(0,1fr)_44px_44px] gap-2 md:hidden">
          <button
            type="button"
            className="flex h-11 min-w-0 items-center justify-center gap-2 rounded-lg border border-[#d9e3ef] bg-[#f8fafc] px-3 text-[13px] font-black text-[#344054] shadow-sm transition hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            onClick={() => setShowMobileFilters((value) => !value)}
            aria-expanded={showMobileFilters}
          >
            <FiFilter className="h-4 w-4 flex-none" />
            <span className="truncate">Filters</span>
            {activeFilterCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-black text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#d9e3ef] bg-white text-[#475467] shadow-sm transition hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            onClick={onRefetch}
            aria-label="Refresh transactions"
            title="Refresh"
          >
            <FiRefreshCw className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:border-emerald-500 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
            onClick={onExport}
            disabled={disableExport}
            aria-label="Export CSV"
            title="Export CSV"
          >
            <FiDownload className="h-4 w-4" />
          </button>
        </div>

        <div className="hidden min-w-0 flex-1 flex-wrap items-center gap-2 md:flex xl:flex-nowrap">
          <Form.Control
            as="select"
            className="min-w-0 border border-primary md:w-[180px] md:flex-none"
            value={filterType}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onFilterTypeChange(e.target.value)
            }
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          <Form.Control
            as="select"
            className="min-w-0 border border-primary md:w-[170px] md:flex-none"
            value={filterStatus}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onFilterStatusChange(e.target.value)
            }
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          <Form.Control
            type="date"
            className="min-w-0 border border-primary md:w-[170px] md:flex-none"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
          />
          <Form.Control
            type="date"
            className="min-w-0 border border-primary md:w-[170px] md:flex-none"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
          />
          <Button
            variant="outline-primary"
            onClick={onRefetch}
            className="flex items-center justify-center gap-1 md:flex-none"
          >
            <FiRefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          {hasAppliedValues && (
            <Button
              variant="outline-secondary"
              onClick={handleClearFilters}
              className="flex items-center justify-center gap-1 md:flex-none"
            >
              <FiX className="h-4 w-4" />
              Clear
            </Button>
          )}
          <div className="hidden flex-1 md:block" />
          <Button
            variant="success"
            onClick={onExport}
            disabled={disableExport}
            className="flex items-center justify-center gap-1 md:flex-none"
          >
            <FiDownload className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <div
        className={`mt-2 border-t border-[#edf1f6] pt-2 dark:border-slate-700 ${
          showMobileFilters ? "block" : "hidden"
        } md:hidden`}
      >
        <div className="grid grid-cols-2 gap-2">
          <label className="min-w-0">
            <span className="mb-1 block text-[11px] font-black uppercase text-slate-500">
              Type
            </span>
            <Form.Control
              as="select"
              className="h-10 min-w-0 rounded-lg border-[#d9e3ef] text-[12px] font-bold shadow-none dark:border-slate-700"
              value={filterType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onFilterTypeChange(e.target.value)
              }
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          </label>
          <label className="min-w-0">
            <span className="mb-1 block text-[11px] font-black uppercase text-slate-500">
              Status
            </span>
            <Form.Control
              as="select"
              className="h-10 min-w-0 rounded-lg border-[#d9e3ef] text-[12px] font-bold shadow-none dark:border-slate-700"
              value={filterStatus}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onFilterStatusChange(e.target.value)
              }
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          </label>
          <label className="min-w-0">
            <span className="mb-1 block text-[11px] font-black uppercase text-slate-500">
              From
            </span>
            <Form.Control
              type="date"
              className="h-10 min-w-0 rounded-lg border-[#d9e3ef] text-[12px] font-bold shadow-none dark:border-slate-700"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
            />
          </label>
          <label className="min-w-0">
            <span className="mb-1 block text-[11px] font-black uppercase text-slate-500">
              To
            </span>
            <Form.Control
              type="date"
              className="h-10 min-w-0 rounded-lg border-[#d9e3ef] text-[12px] font-bold shadow-none dark:border-slate-700"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
            />
          </label>
        </div>

        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            className="flex h-10 items-center justify-center gap-1.5 rounded-lg border border-[#d9e3ef] bg-white px-3 text-[12px] font-black text-[#475467] shadow-sm transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            onClick={handleClearFilters}
            disabled={!hasAppliedValues}
          >
            <FiX className="h-4 w-4" />
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
